import { Component, EventEmitter, inject, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule, NgForm, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { fuseAnimations } from '@fuse/animations';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { AuthService } from 'app/core/auth/auth.service';
import { IAuthResponse } from 'app/shared/model/security.model';
import { hasValue } from 'app/shared';
import { RouterLink } from '@angular/router';
import { ActivityTimerComponent } from 'app/shared/activity-timer/activity-timer.component';

@Component({
    selector     : 'sign-in-form',
    templateUrl  : './sign-in-form.component.html',
    styleUrls    : ['./sign-in-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations   : fuseAnimations,
    imports      : [
        FuseAlertComponent,
        FormsModule,
        ReactiveFormsModule,
        RouterLink,
        ActivityTimerComponent,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatCheckboxModule,
        MatProgressSpinnerModule,
    ]
})
export class SignInFormComponent implements OnInit
{
    private readonly _authService = inject(AuthService);
    private _formBuilder = inject(UntypedFormBuilder);

    @ViewChild('signInNgForm') signInNgForm: NgForm;
    @Output() successEvent: EventEmitter<any> = new EventEmitter<any>();
    @Output() errorEvent: EventEmitter<any> = new EventEmitter<any>();
    @Output() forgetPasswordEvent: EventEmitter<any> = new EventEmitter<any>();

    alert: { type: FuseAlertType; message: string } = {
        type   : 'success',
        message: '',
    };
    signInForm: UntypedFormGroup;
    showAlert: boolean = false;
    passwordExpired: boolean = false;

    /**
     * Constructor
     */
    constructor(
    )
    {
    }

    ngOnInit(): void
    {
        // Create the form
        this.signInForm = this._formBuilder.group({
            username  : ['', [Validators.required]],
            password  : ['', Validators.required],
            keepMeSignedIn: [false]
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sign in
     */
    signIn(): void
    {
        // Return if the form is invalid
        if ( this.signInForm.invalid )
        {
            return;
        }

        // Disable the form
        this.signInForm.disable();

        // Hide the alert
        this.showAlert = false;
        this.passwordExpired = false;

        // Sign in
        this._authService.signIn(this.signInForm.value)
            .subscribe({
                next: (response: IAuthResponse) =>
                {
                    // Re-enable the form
                    this.signInForm.enable();
                    // Reset the form
                    this.signInNgForm.resetForm();

                    this.successEvent.emit(response);
                },
                error: (response) =>
                {
                    console.debug(response);
                    // Re-enable the form
                    this.signInForm.enable();

                    // Reset the form
                    this.signInNgForm.resetForm();

                    // Set the alert
                    let errMsg = 'something went wrong, please try again';
                    let msg = response.result?.message ?? response.message;
                    if(hasValue(msg)) {
                        errMsg = msg
                    }

                    this.passwordExpired = !!response.result?.passwordExpired;

                    this.alert = {
                        type   : 'error',
                        message: errMsg
                    };

                    // Show the alert
                    this.showAlert = true;

                    this.errorEvent.emit(response);
                },
            });
    }

    forgetPassword() {
        this.forgetPasswordEvent.emit();
    }

    // Combined watched value for ActivityTimer (username|password)
    get activityWatchedValue(): string {
        const u = this.signInForm?.get('username')?.value ?? '';
        const p = this.signInForm?.get('password')?.value ?? '';
        return `${u}${p}`;
    }

    onActivityTimedOut(): void {
        // Reset forms due to inactivity
        try { this.signInNgForm?.resetForm(); } catch (e) { /* ignore */ }
        try { this.signInForm?.reset(); } catch (e) { /* ignore */ }
    }
}
